import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { riskService } from '@/services/risk';
import { formatCurrency } from '@/lib/utils';

export function RiskLimitCard() {
  const { data: utilization, isLoading } = useQuery({
    queryKey: ['risk', 'utilization'],
    queryFn: () => riskService.getUtilization(),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse h-32 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  // Return null if no data or missing required fields
  if (!utilization || typeof utilization.budgetUtilizationPct !== 'number') {
    return null;
  }

  const budgetPct = utilization.budgetUtilizationPct;

  const getStatusColor = (pct: number) => {
    if (pct >= 90) return 'text-red-500';
    if (pct >= 70) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getProgressColor = (pct: number) => {
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const StatusIcon = budgetPct >= 90 ? AlertTriangle : budgetPct >= 70 ? Shield : CheckCircle;

  const formatPercentDisplay = (pct: number) => `${pct.toFixed(1)}%`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Risk Status</CardTitle>
        <StatusIcon className={`h-4 w-4 ${getStatusColor(budgetPct)}`} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>Budget Used</span>
            <span className={getStatusColor(budgetPct)}>{formatPercentDisplay(budgetPct)}</span>
          </div>
          <Progress
            value={Math.min(budgetPct, 100)}
            className="h-2"
            indicatorClassName={getProgressColor(budgetPct)}
            role="progressbar"
            aria-valuenow={Math.round(budgetPct)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Budget utilization"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>{formatCurrency(utilization.budgetUsed)}</span>
            <span>{formatCurrency(utilization.budgetTotal)}</span>
          </div>
        </div>

        <div>
          <div className="flex justify-between text-sm mb-1">
            <span>Positions</span>
            <span>
              {utilization.positionsCount} / {utilization.positionsMax}
            </span>
          </div>
          <Progress
            value={
              utilization.positionsMax > 0
                ? (utilization.positionsCount / utilization.positionsMax) * 100
                : 0
            }
            className="h-2"
            role="progressbar"
            aria-valuenow={
              utilization.positionsMax > 0
                ? Math.round((utilization.positionsCount / utilization.positionsMax) * 100)
                : 0
            }
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Positions utilization"
          />
        </div>

        <div className="pt-2 border-t">
          <div className="flex justify-between text-sm">
            <span>Daily Loss</span>
            <span className={utilization.dailyLossCurrent > 0 ? 'text-red-500' : ''}>
              {formatCurrency(utilization.dailyLossCurrent)} /{' '}
              {formatCurrency(utilization.dailyLossLimit)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default RiskLimitCard;
